import type {
  NamedResidentDefinition,
  ResidentScheduleEntry,
} from "./types.ts";
import {
  generateResidentAppearance,
  type ResidentArchetypeId,
} from "../residentAppearance.ts";
import type { CharacterAppearanceOverrides } from "../appearance.ts";

const WORKDAY_SCHEDULE: readonly ResidentScheduleEntry[] = [
  { startHour: 0, endHour: 8, anchor: "home", activity: "home" },
  { startHour: 8, endHour: 11.5, anchor: "street", activity: "walking" },
  { startHour: 11.5, endHour: 15, anchor: "work", activity: "working" },
  { startHour: 15, endHour: 19, anchor: "courtyard", activity: "resting" },
  { startHour: 19, endHour: 24, anchor: "home", activity: "home" },
];
const RETIRED_SCHEDULE: readonly ResidentScheduleEntry[] = [
  { startHour: 0, endHour: 8, anchor: "home", activity: "home" },
  { startHour: 8, endHour: 11.5, anchor: "street", activity: "walking" },
  { startHour: 11.5, endHour: 18, anchor: "courtyard", activity: "resting" },
  { startHour: 18, endHour: 24, anchor: "home", activity: "home" },
];
const NIGHT_BAKER_SCHEDULE: readonly ResidentScheduleEntry[] = [
  { startHour: 0, endHour: 7, anchor: "work", activity: "working" },
  { startHour: 7, endHour: 15, anchor: "home", activity: "home" },
  { startHour: 15, endHour: 19, anchor: "street", activity: "walking" },
  { startHour: 19, endHour: 24, anchor: "work", activity: "working" },
];

const NAMED_RESIDENT_APPEARANCE_OVERRIDES = {
  "ruta-kalniene": {
    ageGroup: "adult",
    hairStyle: "wavy",
    facialHair: "none",
    outerwear: "cardigan",
    lowerGarment: "wide_trousers",
    eyewear: "round_glasses",
    headwear: "none",
    neckwear: "scarf",
    carriedAccessory: "tote",
  },
  "mantas-simkus": {
    ageGroup: "young_adult",
    hairStyle: "cropped",
    facialHair: "none",
    outerwear: "field_jacket",
    lowerGarment: "trousers",
    headwear: "none",
    neckwear: "kerchief",
    carriedAccessory: "none",
  },
  "antanas-valeika": {
    ageGroup: "adult",
    hairStyle: "cropped",
    facialHair: "moustache",
    outerwear: "field_jacket",
    lowerGarment: "trousers",
    headwear: "flat_cap",
    neckwear: "scarf",
    carriedAccessory: "satchel",
  },
  "ieva-zukauskaite": {
    ageGroup: "young_adult",
    hairStyle: "wavy",
    facialHair: "none",
    outerwear: "hoodie",
    lowerGarment: "wide_trousers",
    headwear: "none",
    neckwear: "scarf",
    carriedAccessory: "backpack",
  },
  "darius-noreika": {
    ageGroup: "adult",
    hairStyle: "buzz_cut",
    facialHair: "stubble",
    outerwear: "field_jacket",
    lowerGarment: "trousers",
    headwear: "none",
    neckwear: "none",
    carriedAccessory: "satchel",
  },
  "laima-petrauskiene": {
    ageGroup: "adult",
    hairStyle: "bob",
    facialHair: "none",
    outerwear: "wool_coat",
    lowerGarment: "skirt",
    headwear: "none",
    neckwear: "scarf",
    carriedAccessory: "tote",
  },
  "tomas-bartkus": {
    ageGroup: "adult",
    hairStyle: "parted",
    facialHair: "none",
    outerwear: "suit_jacket",
    lowerGarment: "trousers",
    headwear: "none",
    neckwear: "tie",
    carriedAccessory: "satchel",
  },
  "emilija-urbaite": {
    ageGroup: "young_adult",
    hairStyle: "bun",
    facialHair: "none",
    outerwear: "cardigan",
    lowerGarment: "skirt",
    headwear: "none",
    neckwear: "kerchief",
    carriedAccessory: "tote",
  },
  "saulius-kazlauskas": {
    ageGroup: "adult",
    hairStyle: "cropped",
    facialHair: "short_beard",
    outerwear: "field_jacket",
    lowerGarment: "trousers",
    headwear: "none",
    neckwear: "kerchief",
    carriedAccessory: "none",
  },
} as const satisfies Readonly<
  Record<string, Readonly<CharacterAppearanceOverrides>>
>;

function resident(
  id: string,
  name: string,
  role: string,
  x: number,
  z: number,
  elderVariant: NamedResidentDefinition["elderVariant"],
  appearanceArchetype: ResidentArchetypeId,
  traits: NamedResidentDefinition["traits"],
  coat: number,
  accent: number,
  schedule: readonly ResidentScheduleEntry[] = WORKDAY_SCHEDULE,
): NamedResidentDefinition {
  const elderAppearance = elderVariant === "none"
    ? {}
    : {
        stature: "short" as const,
        ageGroup: "older_adult" as const,
        hairColour: "silver" as const,
        hairStyle: elderVariant === "plum_scarf"
          ? "cropped" as const
          : "bob" as const,
        headwear: elderVariant === "plum_scarf"
          ? "headscarf" as const
          : "none" as const,
        neckwear: elderVariant === "amber_coat"
          ? "kerchief" as const
          : "none" as const,
      };
  const authoredAppearance =
    NAMED_RESIDENT_APPEARANCE_OVERRIDES[
      id as keyof typeof NAMED_RESIDENT_APPEARANCE_OVERRIDES
    ] ?? {};
  return {
    id,
    name,
    role,
    homeDistrict: "old_town",
    elderVariant,
    appearanceArchetype,
    traits,
    anchors: {
      home: { x: x - 12, y: 0, z: z + 10 },
      courtyard: { x: x + 18, y: 0, z: z + 16 },
      street: { x, y: 0, z },
      work: { x: x - 22, y: 0, z: z - 18 },
    },
    schedule,
    clothing: { coat, accent },
    appearance: generateResidentAppearance(id, {
      archetype: appearanceArchetype,
      overrides: {
        ...authoredAppearance,
        ...elderAppearance,
        colours: {
          outerwear: coat,
          accent,
        },
      },
    }),
  };
}

/** Final authored M5 cast; these are fictional residents, not real people. */
export const NAMED_RESIDENTS: readonly NamedResidentDefinition[] = [
  resident("ona-vaitkute", "Ona Vaitkutė", "retired archivist", 459, 389, "amber_coat", "senjore", ["practical", "curious"], 0x76513d, 0xd4a35d, RETIRED_SCHEDULE),
  resident("genovaite-rimkiene", "Genovaitė Rimkienė", "courtyard gardener", 493, 413, "plum_scarf", "senjore", ["traditional", "practical"], 0x594c55, 0x9a6474, RETIRED_SCHEDULE),
  resident("aldona-jukneviciene", "Aldona Juknevičienė", "former tram dispatcher", 420, 445, "blue_cardigan", "active_senior", ["practical", "traditional"], 0x52636b, 0x8ea4aa, RETIRED_SCHEDULE),
  resident("ruta-kalniene", "Rūta Kalnienė", "bookbinder", 407, 468, "none", "creative_local", ["curious", "practical"], 0x586653, 0xb38b60),
  resident("mantas-simkus", "Mantas Šimkus", "café porter", 438, 432, "none", "service_worker", ["curious"], 0x4a5965, 0xc17a4d),
  resident("antanas-valeika", "Antanas Valeika", "courtyard caretaker", 475, 450, "none", "practical_worker", ["traditional", "practical"], 0x625d50, 0x9c8c70),
  resident("ieva-zukauskaite", "Ieva Žukauskaitė", "architecture student", 384, 404, "none", "student", ["curious"], 0x5c5169, 0xb194c1),
  resident("darius-noreika", "Darius Noreika", "bicycle mechanic", 522, 388, "none", "practical_worker", ["practical"], 0x435c58, 0xb1774f),
  resident("laima-petrauskiene", "Laima Petrauskienė", "choir alto", 535, 444, "none", "everyday_local", ["traditional", "curious"], 0x6b4e52, 0xc49b78),
  resident("tomas-bartkus", "Tomas Bartkus", "museum guard", 365, 455, "none", "service_worker", ["practical", "traditional"], 0x4b5359, 0xa79672),
  resident("emilija-urbaite", "Emilija Urbaitė", "florist", 505, 354, "none", "creative_local", ["curious", "practical"], 0x667052, 0xd2a06b),
  resident("saulius-kazlauskas", "Saulius Kazlauskas", "night baker", 395, 365, "none", "service_worker", ["practical"], 0x5f4b42, 0xbda278, NIGHT_BAKER_SCHEDULE),
] as const;
